import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Switch,
  CircularProgress,
  Stack,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save,
  Layers,
  Users,
  ShieldCheck,
  Building2,
  CheckSquare,
  Square,
  UsersRound,
  CreditCard,
  Wallet,
  GraduationCap,
  FileText,
  Calendar,
  Bus,
  Building,
  BookOpen,
  Package,
  Megaphone,
  DollarSign,
  BarChart,
  BadgeCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { useModuleStore } from '@/store/superAdmin/moduleStore';
import { roleAccessService } from '@/services/superAdmin/roleAccessService';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';
import * as Icons from 'lucide-react';

const getModuleIcon = (iconName) => {
  const iconMap = {
    people: Users,
    badge: BadgeCheck,
    payments: CreditCard,
    family_restroom: UsersRound,
    account_balance_wallet: Wallet,
    school: GraduationCap,
    fact_check: CheckSquare,
    assignment: FileText,
    calendar_month: Calendar,
    directions_bus: Bus,
    hotel: Building,
    menu_book: BookOpen,
    inventory: Package,
    campaign: Megaphone,
    money: DollarSign,
    analytics: BarChart,
  };
  return iconMap[iconName] || Layers;
};

export function ModuleAssignmentPage({ defaultTab = 0 }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { globalSchools, fetchAllSchools } = useSuperAdminSchoolStore();

  // Shared School Selection State
  const [selectedSchoolId, setSelectedSchoolId] = useState('');

  // ------------------------------------------------------------
  // TAB 0: ROLE ACCESS PERMISSIONS STATE
  // ------------------------------------------------------------
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [rolesLoading, setRolesLoading] = useState(false);

  const [roleAccessData, setRoleAccessData] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [roleAccessError, setRoleAccessError] = useState(null);

  const [selectedMenuMap, setSelectedMenuMap] = useState({});
  const [isSavingRoleAccess, setIsSavingRoleAccess] = useState(false);

  // ------------------------------------------------------------
  // TAB 1: SCHOOL MODULES ASSIGNMENT STATE
  // ------------------------------------------------------------
  const {
    availableModules,
    assignedModules,
    loading: schoolModulesLoading,
    error: schoolModulesError,
    fetchAvailableModules,
    fetchAssignedModules,
    assignModules,
  } = useModuleStore();

  const [selectedSchoolModules, setSelectedSchoolModules] = useState([]);
  const [isSavingSchoolModules, setIsSavingSchoolModules] = useState(false);

  // Initial load
  useEffect(() => {
    fetchAllSchools();
    fetchAvailableModules();
  }, [fetchAllSchools, fetchAvailableModules]);

  // Fetch Roles & Assigned School Modules when selected school changes
  useEffect(() => {
    if (!selectedSchoolId) {
      setRoles([]);
      setSelectedRoleId('');
      setRoleAccessData(null);
      setSelectedMenuMap({});
      setSelectedSchoolModules([]);
      return;
    }

    // Fetch Roles for Tab 0
    const fetchRoles = async () => {
      setRolesLoading(true);
      setRoleAccessError(null);
      try {
        const rolesList = await roleAccessService.getSchoolRoles(selectedSchoolId);
        setRoles(rolesList);
        if (rolesList.length > 0) {
          setSelectedRoleId(rolesList[0].id);
        } else {
          setSelectedRoleId('');
          setRoleAccessData(null);
        }
      } catch (err) {
        setRoleAccessError(err.message || 'Failed to fetch roles for selected school');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();

    // Fetch Assigned Modules for Tab 1
    fetchAssignedModules(selectedSchoolId);
  }, [selectedSchoolId, fetchAssignedModules]);

  // Sync assigned school modules
  useEffect(() => {
    if (assignedModules) {
      setSelectedSchoolModules(assignedModules.map((m) => m.id));
    }
  }, [assignedModules]);

  // Fetch Role Access tree for Tab 0
  useEffect(() => {
    if (!selectedSchoolId || !selectedRoleId) {
      setRoleAccessData(null);
      setSelectedMenuMap({});
      return;
    }

    const fetchAccess = async () => {
      setAccessLoading(true);
      setRoleAccessError(null);
      try {
        const accessRes = await roleAccessService.getRoleAccess(selectedSchoolId, selectedRoleId);
        setRoleAccessData(accessRes);

        const initialMap = {};
        (accessRes?.modules || []).forEach((mod) => {
          const enabledMenuIds = (mod.menus || [])
            .filter((m) => m.enabled)
            .map((m) => m.id);

          if (mod.enabled) {
            initialMap[mod.id] = enabledMenuIds;
          }
        });
        setSelectedMenuMap(initialMap);
      } catch (err) {
        setRoleAccessError(err.message || 'Failed to fetch role module access');
      } finally {
        setAccessLoading(false);
      }
    };

    fetchAccess();
  }, [selectedSchoolId, selectedRoleId]);

  // ------------------------------------------------------------
  // HANDLERS FOR TAB 0: ROLE ACCESS
  // ------------------------------------------------------------
  const handleToggleRoleModule = (mod) => {
    setSelectedMenuMap((prev) => {
      const next = { ...prev };
      if (mod.id in next) {
        delete next[mod.id];
      } else {
        next[mod.id] = (mod.menus || []).map((m) => m.id);
      }
      return next;
    });
  };

  const handleToggleRoleMenu = (mod, menuId) => {
    setSelectedMenuMap((prev) => {
      const next = { ...prev };
      const currentMenuIds = next[mod.id] ? [...next[mod.id]] : [];

      let updatedMenuIds;
      if (currentMenuIds.includes(menuId)) {
        updatedMenuIds = currentMenuIds.filter((id) => id !== menuId);
      } else {
        updatedMenuIds = [...currentMenuIds, menuId];
      }

      next[mod.id] = updatedMenuIds;
      return next;
    });
  };

  const handleSelectAllRoleAccess = () => {
    const fullMap = {};
    (roleAccessData?.modules || []).forEach((mod) => {
      fullMap[mod.id] = (mod.menus || []).map((m) => m.id);
    });
    setSelectedMenuMap(fullMap);
    toast.success('All modules and menus selected');
  };

  const handleClearAllRoleAccess = () => {
    setSelectedMenuMap({});
    toast.success('All modules and menus cleared');
  };

  const handleSaveRoleAccess = async () => {
    if (!selectedSchoolId || !selectedRoleId) {
      toast.error('Please select both a school and a role');
      return;
    }

    setIsSavingRoleAccess(true);
    try {
      const modulesPayload = (roleAccessData?.modules || [])
        .map((mod) => ({
          module_id: mod.id,
          menu_ids: selectedMenuMap[mod.id] || [],
        }))
        .filter((mod) => Array.isArray(mod.menu_ids) && mod.menu_ids.length > 0);

      const res = await roleAccessService.updateRoleAccess(selectedSchoolId, selectedRoleId, modulesPayload);
      toast.success(res?.message || 'Role access assigned successfully.');
    } catch (err) {
      toast.error(err.message || 'Failed to update role access configuration');
    } finally {
      setIsSavingRoleAccess(false);
    }
  };

  // ------------------------------------------------------------
  // HANDLERS FOR TAB 1: SCHOOL MODULES
  // ------------------------------------------------------------
  const handleToggleSchoolModule = (moduleId) => {
    setSelectedSchoolModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  const handleSaveSchoolModules = async () => {
    if (!selectedSchoolId) {
      toast.error('Please select a school to assign modules.');
      return;
    }
    setIsSavingSchoolModules(true);
    try {
      await assignModules(selectedSchoolId, selectedSchoolModules);
      toast.success('School modules assigned successfully');
    } catch {
      toast.error('Failed to assign modules to school');
    } finally {
      setIsSavingSchoolModules(false);
    }
  };

  const selectedRoleObj = roles.find((r) => r.id === selectedRoleId);

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Module & Role Access Assignment"
        subtitle="Manage role permissions and enable feature modules for schools."
        action={
          activeTab === 0 ? (
            <Button
              variant="contained"
              startIcon={isSavingRoleAccess ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />}
              onClick={handleSaveRoleAccess}
              disabled={!selectedSchoolId || !selectedRoleId || isSavingRoleAccess || accessLoading}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                px: 3,
                textTransform: 'none',
              }}
            >
              {isSavingRoleAccess ? 'Saving...' : 'Save Role Permissions'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={isSavingSchoolModules ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />}
              onClick={handleSaveSchoolModules}
              disabled={!selectedSchoolId || isSavingSchoolModules}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                px: 3,
                textTransform: 'none',
              }}
            >
              {isSavingSchoolModules ? 'Saving...' : 'Save School Modules'}
            </Button>
          )
        }
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab
            icon={<ShieldCheck size={18} />}
            iconPosition="start"
            label="Role Permissions Assignment"
            sx={{ fontWeight: 600, textTransform: 'none' }}
          />
          <Tab
            icon={<Building2 size={18} />}
            iconPosition="start"
            label="School Module Assignment"
            sx={{ fontWeight: 600, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Control Panel: School Selector */}
      <Card
        elevation={0}
        sx={{
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.colors.border}`,
          mb: 3.5,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: activeTab === 0 ? 6 : 12 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Building2 size={16} /> SELECT SCHOOL *
              </Typography>
              <Select
                fullWidth
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value)}
                displayEmpty
                sx={{ borderRadius: tokens.radius.sm }}
              >
                <MenuItem value="" disabled>
                  Choose a school...
                </MenuItem>
                {globalSchools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.school_name || school.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {activeTab === 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShieldCheck size={16} /> SELECT ROLE *
                </Typography>
                <Select
                  fullWidth
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  displayEmpty
                  disabled={!selectedSchoolId || rolesLoading}
                  sx={{ borderRadius: tokens.radius.sm }}
                >
                  <MenuItem value="" disabled>
                    {rolesLoading ? 'Loading roles...' : !selectedSchoolId ? 'Select a school first' : 'Choose a role...'}
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.description || role.name} ({role.name})
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}

            {activeTab === 0 && selectedSchoolId && roles.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 1 }}>
                    Quick Role Select:
                  </Typography>
                  {roles.map((r) => {
                    const isSelected = r.id === selectedRoleId;
                    return (
                      <Chip
                        key={r.id}
                        label={r.description || r.name}
                        onClick={() => setSelectedRoleId(r.id)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        size="small"
                        sx={{ fontWeight: 600, cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* TAB 0 CONTENT: ROLE PERMISSIONS ASSIGNMENT */}
      {activeTab === 0 && (
        <Box>
          {roleAccessError && <ErrorState message={roleAccessError} onRetry={() => setSelectedSchoolId(selectedSchoolId)} />}

          {!selectedSchoolId && !roleAccessError && (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: tokens.radius.md }}>
              <Building2 size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                No School Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select a school above to configure role access permissions.
              </Typography>
            </Paper>
          )}

          {selectedSchoolId && !selectedRoleId && !rolesLoading && (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: tokens.radius.md }}>
              <ShieldCheck size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                No Role Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please choose a role above to configure module and menu access.
              </Typography>
            </Paper>
          )}

          {accessLoading && <Loader message="Loading role module permissions..." />}

          {selectedSchoolId && selectedRoleId && !accessLoading && roleAccessData && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Layers size={22} color={tokens.colors.primary.main} /> Module Permissions for
                  </Typography>
                  <Chip
                    label={selectedRoleObj?.description || roleAccessData.role_name}
                    color="primary"
                    sx={{ fontWeight: 700, fontSize: 13 }}
                  />
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckSquare size={16} />}
                    onClick={handleSelectAllRoleAccess}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Square size={16} />}
                    onClick={handleClearAllRoleAccess}
                  >
                    Clear All
                  </Button>
                </Stack>
              </Box>

              <Grid container spacing={3}>
                {(roleAccessData.modules || []).map((mod) => {
                  const isModuleEnabled = mod.id in selectedMenuMap;
                  const enabledMenus = selectedMenuMap[mod.id] || [];
                  const IconComponent = getModuleIcon(mod.icon);

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mod.id}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: tokens.radius.md,
                          border: `1px solid ${isModuleEnabled ? tokens.colors.primary.main : tokens.colors.border}`,
                          backgroundColor: isModuleEnabled ? `${tokens.colors.primary.main}08` : 'background.paper',
                          transition: 'all 0.2s ease',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: tokens.radius.sm,
                                  backgroundColor: isModuleEnabled ? tokens.colors.primary.main : tokens.colors.grey[100],
                                  color: isModuleEnabled ? '#fff' : tokens.colors.text.secondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <IconComponent size={22} />
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                  {mod.display_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {mod.name}
                                </Typography>
                              </Box>
                            </Box>

                            <Switch
                              checked={isModuleEnabled}
                              onChange={() => handleToggleRoleModule(mod)}
                              color="primary"
                            />
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            NAVIGATION MENUS ({enabledMenus.length} / {mod.menus ? mod.menus.length : 0})
                          </Typography>

                          {(!mod.menus || mod.menus.length === 0) ? (
                            <Typography variant="body2" color="text.secondary" sx={{ italic: true, py: 1 }}>
                              No sub-menus configured for this module.
                            </Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              {mod.menus.map((menu) => {
                                const isMenuChecked = enabledMenus.includes(menu.id);
                                return (
                                  <FormControlLabel
                                    key={menu.id}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={isMenuChecked}
                                        onChange={() => handleToggleRoleMenu(mod, menu.id)}
                                        color="primary"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" fontWeight={isMenuChecked ? 600 : 400}>
                                        {menu.display_name}
                                      </Typography>
                                    }
                                    sx={{ ml: -0.5, my: -0.2 }}
                                  />
                                );
                              })}
                            </Stack>
                          )}
                        </CardContent>

                        <Box sx={{ px: 3, pb: 2, pt: 0 }}>
                          <Chip
                            label={isModuleEnabled ? 'ENABLED FOR ROLE' : 'DISABLED'}
                            size="small"
                            color={isModuleEnabled ? 'success' : 'default'}
                            variant={isModuleEnabled ? 'filled' : 'outlined'}
                            sx={{ fontSize: 10, fontWeight: 700, borderRadius: tokens.radius.xs }}
                          />
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 1 CONTENT: SCHOOL MODULE ASSIGNMENT */}
      {activeTab === 1 && (
        <Box>
          {schoolModulesError && !selectedSchoolId && (
            <ErrorState message={schoolModulesError} onRetry={fetchAvailableModules} />
          )}

          {!selectedSchoolId && (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: tokens.radius.md }}>
              <Building2 size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                No School Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select a school above to view and assign school-level feature modules.
              </Typography>
            </Paper>
          )}

          {selectedSchoolId && (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Layers size={20} color={tokens.colors.primary.main} /> Available System Modules
              </Typography>

              {schoolModulesLoading ? (
                <Loader message="Loading available modules..." />
              ) : (
                <Grid container spacing={3}>
                  {availableModules.map((module) => {
                    const isSelected = selectedSchoolModules.includes(module.id);
                    const IconComponent = Icons[module.icon] || Icons.Box;

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={module.id}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: tokens.radius.md,
                            border: `1px solid ${isSelected ? tokens.colors.primary.main : tokens.colors.border}`,
                            backgroundColor: isSelected ? `${tokens.colors.primary.main}08` : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: tokens.colors.primary.main,
                              transform: 'translateY(-2px)',
                            },
                          }}
                          onClick={() => handleToggleSchoolModule(module.id)}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: tokens.radius.sm,
                                  backgroundColor: isSelected ? tokens.colors.primary.main : tokens.colors.grey[100],
                                  color: isSelected ? '#fff' : tokens.colors.text.secondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <IconComponent size={22} />
                              </Box>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleToggleSchoolModule(module.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                  />
                                }
                                label=""
                                sx={{ m: 0 }}
                              />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                              {module.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {module.name}
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              {module.menus ? module.menus.length : 0} Navigation Menus Included
                            </Typography>
                            {module.menus && module.menus.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {module.menus.map((m) => (
                                  <Chip
                                    key={m.id}
                                    label={m.display_name}
                                    size="small"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 22,
                                      backgroundColor: isSelected ? `${tokens.colors.primary.main}15` : tokens.colors.grey[100],
                                      color: isSelected ? tokens.colors.primary.main : tokens.colors.text.primary,
                                      fontWeight: 500,
                                      borderRadius: tokens.radius.sm,
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
